import React from "react";
import { Link } from "react-router-dom";
import { formatOrderDate } from "../../../../lib/formatOrderDate";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";
import {
  BsPencil,
  BsEnvelope,
  BsClipboardPlus,
  BsCreditCard2Back,
  BsTrash,
} from "react-icons/bs";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  Order,
  OrderDetailsInfo,
  DumpsterSize,
  MaterialDisposing,
  PaymentType,
  dumpsterSizeToText,
  materialDisposingToText,
  paymentTypeToText,
} from "../../../../models";
import {
  setShowDeleteOrderModal,
  setShowUpdateOrderModal,
  setShowPickupInstructionsModal,
  setShowUpdatePaymentTypeModal,
  sendConfirmationEmail,
} from "../../ordersSlice";
import { OrderTimeline } from "../OrderTimeline/OrderTimeline";
import { LinkCustomer } from "../LinkCustomer/LinkCustomer";
import { PhoneNumber } from "../../../../components";

import "./OrderDetailsTimeline.scss";

interface Props {
  order: Order;
  orderDetails?: OrderDetailsInfo;
}

// Layout A — the lifecycle-timeline order view. Dark header + order-summary
// card + a vertical timeline (delivery / pickup / dump / billing). Actions
// dispatch the same modals as the legacy card (all mounted in Layout), so the
// toggle can swap between this and the old view with no behavioral difference.
export function OrderDetailsTimeline({ order, orderDetails }: Props) {
  const dispatch = useAppDispatch();
  const sendConfirmationEmailAPI = useAppSelector(
    (state) => state.orders.sendConfirmationEmailAPI
  );
  const emailLoading = sendConfirmationEmailAPI.loading;
  const emailHasError = !!sendConfirmationEmailAPI.error;
  const emailHasValue = !!sendConfirmationEmailAPI.value;

  React.useEffect(() => {
    if (!emailLoading && emailHasError) {
      toast.error("Something went wrong. Please try again.");
    }
    if (!emailLoading && emailHasValue) {
      toast.success("Successfully sent confirmation email.");
    }
  }, [emailLoading, emailHasError, emailHasValue]);

  const total =
    order.price + order.deliveryFee + order.additionalCharges - order.discount;

  return (
    <div className="order-details-timeline">
      {/* HEADER */}
      <div className="odt-header">
        <div className="odt-lead">
          <span className="odt-ordno">Order #{order.id}</span>
          <span className="odt-cust">{order.name}</span>
          {(order.phoneNumber || order.email) && (
            <div className="odt-contact">
              {order.phoneNumber && (
                <span className="odt-contact-item">
                  <span className="odt-contact-ic">📞</span>
                  <PhoneNumber phoneNumber={order.phoneNumber} />
                </span>
              )}
              {order.email && (
                <span className="odt-contact-item">
                  <span className="odt-contact-ic">✉️</span>
                  {order.email}
                </span>
              )}
            </div>
          )}
          {(order.dumpsterSize || order.materialDisposing || order.po) && (
            <div className="odt-fact-chips">
              {order.dumpsterSize && (
                <span className="odt-chip">
                  <span className="odt-chip-k">Size</span>
                  {dumpsterSizeToText[order.dumpsterSize as DumpsterSize]}
                </span>
              )}
              {order.materialDisposing && (
                <span className="odt-chip">
                  <span className="odt-chip-k">Material</span>
                  {materialDisposingToText[order.materialDisposing as MaterialDisposing]}
                </span>
              )}
              {order.po && (
                <span className="odt-chip">
                  <span className="odt-chip-k">PO</span>
                  {order.po}
                </span>
              )}
            </div>
          )}
          <LinkCustomer order={order} />
        </div>
        <div className="odt-actions">
          <span className="odt-status-pill">● {order.status}</span>
          <div className="odt-btnrow">
            <button
              className="odt-btn"
              onClick={() => dispatch(setShowUpdateOrderModal(order))}
            >
              <BsPencil className="odt-btn-icon" /> Update
            </button>
            <button
              className="odt-btn"
              onClick={() => dispatch(sendConfirmationEmail({ id: order.id }))}
            >
              <BsEnvelope className="odt-btn-icon" /> Confirmation
              {sendConfirmationEmailAPI.loading && (
                <span style={{ marginLeft: 6, display: "inline-flex" }}>
                  <Oval color="#FBD20A" height={14} width={14} secondaryColor="#131313" />
                </span>
              )}
            </button>
            <button
              className="odt-btn"
              onClick={() => dispatch(setShowPickupInstructionsModal(order.id))}
            >
              <BsClipboardPlus className="odt-btn-icon" /> Pickup instr.
            </button>
            <button
              className="odt-btn"
              onClick={() => dispatch(setShowUpdatePaymentTypeModal(order))}
            >
              <BsCreditCard2Back className="odt-btn-icon" /> Payment
            </button>
            <button
              className="odt-btn danger"
              onClick={() => dispatch(setShowDeleteOrderModal(order.id))}
            >
              <BsTrash className="odt-btn-icon" /> Cancel
            </button>
          </div>
        </div>
      </div>

      {/* SUMMARY */}
      <div className="odt-summary">
        <h3 className="odt-section-title">Order Summary</h3>
        <div className="odt-grid">
          <div className="kv">
            <span className="k">Address</span>
            <span className="v">{order.address || "—"}</span>
          </div>
          <div className="kv">
            <span className="k">City / Zip</span>
            <span className="v">
              {order.city || "—"} · {order.zip || "—"}
            </span>
          </div>
          <div className="kv">
            <span className="k">Dumpster</span>
            <span className="v">
              {order.dumpsterId ? (
                <Link to={`/dumpsters/${order.dumpsterId}`}>
                  {order.dumpsterLabel || "View"}
                </Link>
              ) : (
                order.dumpsterLabel || "—"
              )}
            </span>
          </div>
          <div className="kv">
            <span className="k">Service</span>
            <span className="v">{order.service || "—"}</span>
          </div>
          <div className="kv">
            <span className="k">Delivery date</span>
            <span className="v">{formatOrderDate(order.deliveryDate)}</span>
          </div>
          <div className="kv">
            <span className="k">Pickup date</span>
            <span className="v">{formatOrderDate(order.pickupDate)}</span>
          </div>
          <div className="kv">
            <span className="k">Payment</span>
            <span className="v">
              {order.paymentType
                ? paymentTypeToText[order.paymentType as PaymentType]
                : "—"}
            </span>
          </div>
          <div className="kv">
            <span className="k">Special instr.</span>
            <span className="v">{order.specialInstructions || "—"}</span>
          </div>
        </div>
        <div className="odt-pricing">
          <span>Base <b>${order.price}</b></span>
          <span>Delivery <b>${order.deliveryFee}</b></span>
          <span>Discount <b>${order.discount}</b></span>
          <span>Add’l <b>${order.additionalCharges}</b></span>
          <span className="tot">Total ${total}</span>
        </div>
      </div>

      {/* TIMELINE */}
      <OrderTimeline order={order} orderDetails={orderDetails} />
    </div>
  );
}
